import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Sparkles, GraduationCap, Users, Calendar, HandshakeIcon, Star, Quote, Crown, BookOpen, Briefcase, Heart, Lightbulb, ShieldCheck, Check, Loader2 } from "lucide-react";
import { HOME } from "@/constants/testIds";
import InquiryModal from "@/components/InquiryModal";

const GOLD = "#D4AF37";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDIA = {
  hero: "https://static.prod-images.emergentagent.com/jobs/26e334a1-cb6a-43f6-a9dc-5c2697b23dc7/images/0c755c7ca3e9b40b32587e843882f4b6fa1a5dc49596ee4469b5e7f2f28937c2.png",
  founder: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/a4qyb59j_A31C5701-8A7B-492A-9A58-0306AD7B1F7D.png",
  logo: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/mwifc6qr_IMG_7515.jpeg",
  community: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/mjwoqpqa_CC9162F7-017D-44FE-9D0C-2BABD4046FA6.png",
  magazine: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/ku82u8hl_IMG_7514.jpeg",
  mixerLogo: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/kgw5cogf_IMG_7160.jpeg",
  mixerTicket: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/ys0co1r8_IMG_7159.jpeg",
  mixerFloral: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/ekom1jcp_IMG_7234.jpeg",
  mixerNeon: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/n3mte65q_IMG_7236.jpeg",
  mixerTrio: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/yeaepbiv_IMG_7248.png",
  academy: "https://customer-assets.emergentagent.com/job_site-builder-3163/artifacts/xpsjr40u_95E79393-39BF-413E-9E4F-A2C461E485EF.png",
  artistic: "https://static.prod-images.emergentagent.com/jobs/26e334a1-cb6a-43f6-a9dc-5c2697b23dc7/images/831a8360408a48296a862a7efb0d771b5a952452fc4bcfda8dec2229518625d3.png",
  texture: "https://static.prod-images.emergentagent.com/jobs/26e334a1-cb6a-43f6-a9dc-5c2697b23dc7/images/a81e748e060bbeebdc893d91f9c9c9271b7e23fa0c2d0b06a0ab8f6a9b8de216.png",
  salon: "https://images.pexels.com/photos/13068357/pexels-photo-13068357.jpeg",
  student: "https://images.pexels.com/photos/30645898/pexels-photo-30645898.jpeg",
};

// ---------------- NAV ----------------
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "About", href: "#about" },
    { label: "Mission", href: "#mission" },
    { label: "Programs", href: "#programs" },
    { label: "Impact", href: "#impact" },
    { label: "Founder", href: "#founder" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <a href="#top" data-testid={HOME.navLogo} className="flex items-center gap-3 group">
          <div className="w-12 h-12 overflow-hidden border border-[#D4AF37]/40 group-hover:border-[#D4AF37] transition-colors duration-500 bg-[#15110a]">
            <img src={MEDIA.logo} alt="The Braid Summit logo" className="w-full h-full object-cover" />
          </div>
          <div className="leading-none">
            <div className="font-display text-lg tracking-tight">The Braid Summit</div>
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37]/80 uppercase mt-0.5">B-Corp · Est. 2024</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-sm tracking-wide text-white/70 hover:text-[#D4AF37] transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#D4AF37] group-hover:w-full transition-all duration-500" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="#join"
            data-testid={HOME.navJoinMovementBtn}
            className="group inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-sm font-medium tracking-wide hover:bg-[#b8860b] transition-all duration-300"
          >
            Join The Movement
            <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
          </a>
        </div>

        <button
          data-testid={HOME.mobileMenuToggle}
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle Menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)} className="text-white/80 text-lg font-display">
                  {l.label}
                </a>
              ))}
              <a
                href="#join"
                onClick={() => setMenuOpen(false)}
                className="bg-[#D4AF37] text-black px-6 py-3 text-sm font-medium tracking-wide w-fit"
              >
                Join The Movement
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// ---------------- HERO ----------------
const Hero = () => (
  <section id="top" className="relative min-h-[100vh] w-full overflow-hidden grain-overlay">
    <div className="absolute inset-0">
      <img src={MEDIA.hero} alt="Braid Summit Hero" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
    </div>

    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-44 pb-32 min-h-[100vh] flex flex-col justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="flex items-center gap-3 mb-8"
      >
        <div className="w-12 h-px bg-[#D4AF37]" />
        <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">A B-Corporation Initiative</span>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.15 }}
        className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight max-w-[1100px]"
      >
        Elevating <em className="text-[#D4AF37] not-italic font-normal italic-display">Braiders</em>.
        <br />
        Empowering Braid Professionals.
        <br />
        <span className="text-white/70">Transforming Communities.</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.3 }}
        className="mt-10 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
      >
        The Braid Summit exists to bridge the gap between talent and opportunity — providing
        education, resources, mentorship, and business development for current and aspiring
        braiders.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.45 }}
        className="mt-12 flex flex-col sm:flex-row items-start gap-5"
      >
        <a
          href="#join"
          data-testid={HOME.heroCtaBtn}
          className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-9 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#b8860b] transition-all duration-300 gold-glow"
        >
          Join The Movement
          <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
        </a>
        <a
          href="#about"
          data-testid={HOME.heroSecondaryBtn}
          className="group inline-flex items-center gap-3 border border-[#D4AF37]/60 text-white px-9 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all duration-300"
        >
          Discover Our Story
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.7 }}
        className="absolute bottom-12 left-6 md:left-12 flex items-center gap-4"
      >
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />
        <div className="text-[10px] tracking-[0.4em] text-white/50 uppercase">Scroll</div>
      </motion.div>
    </div>
  </section>
);

// ---------------- ABOUT ----------------
const About = () => (
  <section id="about" className="relative py-32 md:py-44 px-6 md:px-12 bg-[#0b0805]">
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="lg:col-span-5 lg:sticky lg:top-32 h-fit"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-8 h-px bg-[#D4AF37]" />
          <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">01 — About</span>
        </div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          A platform built for <em className="text-[#D4AF37] not-italic">talent</em>, equipped for legacy.
        </h2>

        <div className="mt-12 relative aspect-square overflow-hidden bg-[#d8c9b1] border border-[#D4AF37]/30">
          <img src={MEDIA.magazine} alt="The Braid Summit Mission Statement" className="w-full h-full object-contain" />
        </div>
        <div className="mt-6 p-6 border border-[#D4AF37]/30 bg-[#15110a]">
          <div className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-2">Our Promise</div>
          <p className="font-display text-lg italic text-white/90">"Knowledge is the new luxury."</p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="lg:col-span-7 space-y-8 lg:pt-20"
      >
        <p className="text-xl md:text-2xl font-display text-white/90 leading-relaxed">
          The Braid Summit was founded to create a safe and empowering space where braiders and braid
          professionals can access the knowledge, resources, and connections needed to build sustainable
          careers and thriving businesses.
        </p>
        <p className="text-base md:text-lg text-white/60 leading-relaxed">
          What began as a vision to support braiders has evolved into a platform focused on
          education, workforce development, entrepreneurship, leadership, and economic empowerment
          within the beauty industry.
        </p>

        <div className="grid sm:grid-cols-3 gap-px bg-white/10 mt-16">
          {[
            { num: "500+", label: "Professionals Trained" },
            { num: "12", label: "Cities & Communities" },
            { num: "$2M+", label: "Economic Impact" },
          ].map((s) => (
            <div key={s.label} className="bg-[#0b0805] p-8">
              <div className="font-display text-4xl md:text-5xl text-[#D4AF37]">{s.num}</div>
              <div className="mt-3 text-xs tracking-[0.25em] uppercase text-white/60">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// ---------------- MISSION & VISION ----------------
const MissionVision = () => (
  <section id="mission" className="relative py-32 md:py-44 overflow-hidden warm-glow">
    <div className="absolute inset-0 opacity-25">
      <img src={MEDIA.texture} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b0805] via-transparent to-[#0b0805]" />
    </div>

    <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-px bg-white/10">
      {[
        {
          tag: "02 — Mission",
          icon: <Sparkles size={28} className="text-[#D4AF37]" />,
          title: "Our Mission",
          body:
            "The Braid Summit is a dynamic gathering focused on empowering and elevating braiding and beauty professionals alike. Our mission is clear: to equip attendees with the strategies, knowledge, and tools necessary to enhance their business performance and succeed in the dynamic beauty industry. With a commitment to fostering creativity and inclusivity within the community, the summit provides a platform for knowledge sharing, inspiration, and celebration. Through panel discussions and interactive sessions, participants will gain valuable insights, network directly with vendors, and celebrate the rich history and dynamic trends of braiding entrepreneurship and styles. Join us for an unforgettable experience dedicated to shaping the future of the braiding industry and celebrating the talent and passion of braiding professionals everywhere.",
        },
        {
          tag: "03 — Vision",
          icon: <Crown size={28} className="text-[#D4AF37]" />,
          title: "Our Vision",
          body:
            "To become the leading platform for beauty industry education, entrepreneurship, and workforce development — creating pathways for economic growth, professional excellence, and community transformation worldwide.",
        },
      ].map((b, i) => (
        <motion.div
          key={b.title}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: i * 0.15 }}
          className="bg-[#15110a] p-10 md:p-16 group hover:bg-[#1a150e] transition-colors duration-500"
        >
          <div className="flex items-center gap-3 mb-10">
            <div className="w-8 h-px bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">{b.tag}</span>
          </div>
          <div className="mb-8">{b.icon}</div>
          <h3 className="font-display text-4xl md:text-5xl mb-8 tracking-tight">{b.title}</h3>
          <p className="text-white/65 leading-relaxed text-lg">{b.body}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

// ---------------- CORE VALUES MARQUEE ----------------
const ValuesMarquee = () => {
  const values = ["Excellence", "Education", "Professionalism", "Community", "Leadership", "Empowerment", "Innovation", "Integrity"];
  const doubled = [...values, ...values, ...values];

  return (
    <section className="relative py-24 border-y border-white/10 bg-[#100c07] overflow-hidden">
      <div className="absolute top-6 left-6 md:left-12 flex items-center gap-3 z-10">
        <div className="w-8 h-px bg-[#D4AF37]" />
        <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">04 — Core Values</span>
      </div>
      <div className="overflow-hidden mt-8">
        <div className="marquee-track flex gap-16 whitespace-nowrap w-max">
          {doubled.map((v, i) => (
            <div key={`${v}-${i}`} className="flex items-center gap-16">
              <span className="font-display text-5xl md:text-7xl lg:text-8xl text-white/8 hover:text-[#D4AF37] transition-colors duration-500" style={{ WebkitTextStroke: '1px rgba(212,175,55,0.4)', color: 'transparent' }}>
                {v}
              </span>
              <span className="text-[#D4AF37] text-4xl">✦</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- IMPACT ----------------
const Impact = () => {
  const commitments = [
    { icon: <BookOpen size={22} />, text: "Providing industry-leading education" },
    { icon: <Briefcase size={22} />, text: "Supporting entrepreneurship & business ownership" },
    { icon: <Users size={22} />, text: "Creating workforce development opportunities" },
    { icon: <Crown size={22} />, text: "Building confidence and leadership skills" },
    { icon: <Heart size={22} />, text: "Strengthening communities through economic empowerment" },
    { icon: <Lightbulb size={22} />, text: "Connecting professionals with valuable resources & networks" },
  ];

  return (
    <section id="impact" className="relative py-32 md:py-44 px-6 md:px-12 bg-[#0b0805]">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">05 — Impact</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Committed to <em className="text-[#D4AF37] not-italic">transformation</em> — at every level.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-8">
            <p className="text-lg text-white/60 leading-relaxed">
              The Braid Summit operates with intention. Each commitment below shapes the way we
              design programs, partner with communities, and deliver outcomes that compound over generations.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {commitments.map((c, i) => (
            <motion.div
              key={c.text}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-[#15110a] p-10 group hover:bg-[#1a150e] transition-colors duration-500 cursor-default"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-all duration-500">
                  {c.icon}
                </div>
                <span className="font-display text-3xl text-white/15 group-hover:text-[#D4AF37]/60 transition-colors duration-500">0{i + 1}</span>
              </div>
              <p className="text-white/85 text-lg leading-snug font-display">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- PROGRAMS ----------------
const Programs = () => {
  const programs = [
    {
      title: "The Braid Summit Academy",
      img: MEDIA.academy,
      desc: "Professional braiding education and business development programs designed to develop both technical mastery and entrepreneurial fluency.",
      tag: "Flagship Program",
    },
    {
      title: "Youth Development Programs",
      img: MEDIA.artistic,
      desc: "Empowering the next generation through mentorship, creativity, and career exploration in the beauty industry.",
      tag: "Next Generation",
    },
    {
      title: "Workshops & Events",
      img: MEDIA.mixerFloral,
      desc: "Curated educational experiences designed to inspire growth, networking, and leadership across the beauty community.",
      tag: "Live Experiences",
    },
    {
      title: "Community Partnerships",
      img: MEDIA.community,
      desc: "Collaborations with brands, schools, and civic leaders that create opportunities and expand access to education.",
      tag: "Collaboration",
      fit: "cover",
      objectPos: "center 30%",
    },
  ];

  return (
    <section id="programs" className="relative py-32 md:py-44 px-6 md:px-12 bg-[#100c07]">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">06 — Programs</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Four pillars. <em className="text-[#D4AF37] not-italic">One ecosystem</em> for excellence.
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          {programs.map((p, i) => {
            const spans = [
              "md:col-span-7 md:row-span-2",
              "md:col-span-5",
              "md:col-span-5",
              "md:col-span-7",
            ];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className={`group relative overflow-hidden cursor-pointer ${spans[i]}`}
              >
                <div className={`relative aspect-[4/3] md:aspect-auto md:h-full min-h-[340px] overflow-hidden ${p.bg || ""}`}>
                  <img
                    src={p.img}
                    alt={p.title}
                    style={p.objectPos ? { objectPosition: p.objectPos } : undefined}
                    className={`absolute inset-0 w-full h-full ${p.fit === "contain" ? "object-contain" : "object-cover"} group-hover:scale-105 transition-transform duration-[1200ms]`}
                  />
                  <div className={`absolute inset-0 ${p.fit === "contain" ? "bg-gradient-to-t from-black via-black/85 via-30% to-transparent" : "bg-gradient-to-t from-black via-black/40 to-transparent"}`} />

                  <div className="absolute top-6 right-6 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] bg-black/60 backdrop-blur-sm px-3 py-1.5 border border-[#D4AF37]/40">
                    {p.tag}
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                    <h3 className="font-display text-2xl md:text-3xl lg:text-4xl mb-4 tracking-tight">{p.title}</h3>
                    <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-xl">{p.desc}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] text-xs tracking-[0.3em] uppercase group-hover:gap-4 transition-all">
                      Learn More <ArrowUpRight size={14} />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------------- SUBSCRIBE FORM ----------------
const SubscribeForm = ({ source = "mixer" }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/subscribe`, { email, source });
      setStatus("success");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid={`subscribe-success-${source}`} className="flex items-center gap-3 border border-[#D4AF37] bg-[#D4AF37]/10 px-6 py-4 text-sm tracking-wide">
        <Check size={18} className="text-[#D4AF37]" />
        <span className="text-white">You're on the list. Check your inbox for confirmation.</span>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="w-full md:w-auto md:min-w-[420px]">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          data-testid={`subscribe-input-${source}`}
          aria-label="Email address"
          className="flex-1 bg-transparent border border-white/20 focus:border-[#D4AF37] outline-none px-5 py-4 text-sm text-white placeholder-white/40 tracking-wide transition-colors duration-300"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid={`subscribe-submit-${source}`}
          className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
        >
          {status === "loading" ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Sending
            </>
          ) : (
            <>
              Get Notified <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
            </>
          )}
        </button>
      </div>
      {status === "error" && (
        <p data-testid={`subscribe-error-${source}`} className="mt-3 text-xs text-red-400 tracking-wide">{errorMsg}</p>
      )}
    </form>
  );
};


// ---------------- CHAMPAGNE QUOTE (cream pop section) ----------------
const ChampagneQuote = () => (
  <section className="relative py-28 md:py-36 px-6 md:px-12 champagne-bg overflow-hidden">
    {/* Subtle gold orbs */}
    <div className="absolute top-0 left-0 w-[40%] h-[60%] pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.18), transparent)" }} />
    <div className="absolute bottom-0 right-0 w-[40%] h-[60%] pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(229,185,152,0.22), transparent)" }} />

    <div className="relative z-10 max-w-[1200px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="lg:col-span-7"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-px bg-[#8a6a1f]" />
          <span className="text-[#8a6a1f] text-xs tracking-[0.4em] uppercase">A Word From The Founder</span>
        </div>
        <Quote size={32} className="text-[#8a6a1f] mb-6" />
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#1a120a]">
          We're not just teaching technique — we're <em className="text-[#8a6a1f] italic">building</em> the next chapter of the beauty industry.
        </h2>
        <div className="mt-10 flex items-center gap-4">
          <div className="w-12 h-12 overflow-hidden rounded-full border border-[#8a6a1f]/40">
            <img src={MEDIA.founder} alt="Tia Smith" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="font-display text-base text-[#1a120a]">Tia Smith</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#8a6a1f] mt-1">Founder & CEO · The Braid Summit</div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="lg:col-span-5"
      >
        <div className="grid grid-cols-2 gap-px bg-[#8a6a1f]/30">
          {[
            { num: "B-Corp", label: "Certified Mission" },
            { num: "100%", label: "Black-Founded" },
            { num: "12+", label: "Cities Reached" },
            { num: "5★", label: "Avg Graduate Rating" },
          ].map((s) => (
            <div key={s.label} className="champagne-bg p-6">
              <div className="font-display text-2xl md:text-3xl text-[#1a120a]">{s.num}</div>
              <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#8a6a1f]">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);


// ---------------- MIXER RECAP ----------------
const MixerRecap = () => (
  <section id="mixer" className="relative py-32 md:py-44 px-6 md:px-12 bg-[#100c07] overflow-hidden warm-glow">
    <div className="max-w-[1400px] mx-auto">
      <div className="grid lg:grid-cols-12 gap-12 mb-16 items-end">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">Event Recap</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            The <em className="text-[#D4AF37] not-italic">Mixer</em> — a night for the craft.
          </h2>
        </div>
        <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[#D4AF37]/30">
          <p className="text-white/65 leading-relaxed">
            An intimate evening of connection, conversation, and celebration — bringing braid
            professionals, vendors, and creatives together inside Baltimore's Luxe Content Studio.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-px bg-white/10">
            <div className="bg-[#100c07] p-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">Date</div>
              <div className="font-display text-base text-[#D4AF37]">Sept 2</div>
            </div>
            <div className="bg-[#100c07] p-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">Time</div>
              <div className="font-display text-base text-[#D4AF37]">5:00 PM</div>
            </div>
            <div className="bg-[#100c07] p-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">Venue</div>
              <div className="font-display text-base text-[#D4AF37]">Towson, MD</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="md:col-span-7 md:row-span-2 relative aspect-[4/5] md:aspect-auto md:h-full min-h-[420px] overflow-hidden group"
        >
          <img src={MEDIA.mixerTrio} alt="The Braid Summit Mixer — group portrait" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">The Crew</div>
            <p className="font-display text-2xl md:text-3xl leading-tight">Editorial portrait — Braid Summit Mixer 2024</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="md:col-span-5 relative aspect-[4/3] overflow-hidden group bg-gradient-to-br from-[#ff7a00] via-[#ff2db4] to-[#a000ff] p-1"
        >
          <img src={MEDIA.mixerLogo} alt="The Braid Summit Mixer logo" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="md:col-span-5 relative aspect-[4/3] overflow-hidden group"
        >
          <img src={MEDIA.mixerNeon} alt="Neon — It was always you" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-5 left-5 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">"It was always you"</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="md:col-span-7 relative aspect-[16/9] overflow-hidden group"
        >
          <img src={MEDIA.mixerFloral} alt="Floral arch portrait" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-5 left-5 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Floral Garden Set</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="md:col-span-5 relative aspect-[4/5] overflow-hidden group bg-[#1a0a1f]"
        >
          <img src={MEDIA.mixerTicket} alt="Braid Summit Mixer admission ticket" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
        </motion.div>
      </div>

      <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t border-white/10 pt-10">
        <div className="md:max-w-md">
          <div className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-3">Stay In The Loop</div>
          <p className="text-white/70 leading-relaxed">
            The next Mixer is in the works. Drop your email to be first in line when invitations open.
          </p>
        </div>
        <SubscribeForm source="mixer" />
      </div>
    </div>
  </section>
);


// ---------------- IVORY PROMISES (cream pop section #2) ----------------
const IvoryPromises = () => {
  const promises = [
    {
      n: "I.",
      title: "Education First.",
      body: "Every program is rooted in real classroom hours, real curriculum, and instructors with measurable results — not influencer fluff.",
    },
    {
      n: "II.",
      title: "Wealth Over Wages.",
      body: "We don't just train braiders to earn — we train them to build assets, hire teams, and create generational wealth.",
    },
    {
      n: "III.",
      title: "Community Always.",
      body: "Every graduate, sponsor, and partner becomes part of a network that opens doors long after the last summit ends.",
    },
  ];

  return (
    <section className="relative py-28 md:py-36 px-6 md:px-12 champagne-bg overflow-hidden">
      {/* Decorative warm orbs */}
      <div className="absolute -top-32 -right-20 w-[55%] h-[80%] pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.18), transparent)" }} />
      <div className="absolute -bottom-20 -left-20 w-[50%] h-[70%] pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(229,185,152,0.18), transparent)" }} />

      <div className="relative z-10 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-[#8a6a1f]" />
              <span className="text-[#8a6a1f] text-xs tracking-[0.4em] uppercase">Our Three Promises</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1a120a]">
              What we <em className="italic text-[#8a6a1f]">stand for</em>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[#8a6a1f]/30">
            <p className="text-[#1a120a]/70 leading-relaxed">
              The Braid Summit isn't a one-time event — it's a movement built on three non-negotiables that
              shape every program, partnership, and decision we make.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#8a6a1f]/30">
          {promises.map((p, i) => (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="champagne-bg p-10 md:p-12 group hover:bg-[#f0e3c8] transition-colors duration-500"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="font-display text-5xl md:text-6xl text-[#8a6a1f] leading-none">{p.n}</span>
                <div className="w-10 h-px bg-[#8a6a1f]/40 mt-6" />
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-4 leading-tight text-[#1a120a]">{p.title}</h3>
              <p className="text-[#1a120a]/65 leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-[#8a6a1f]/20">
          <p className="font-display italic text-xl md:text-2xl text-[#1a120a]/80 max-w-2xl">
            "We measure our success not by attendance — but by the careers we change and the communities we lift."
          </p>
          <a
            href="#founder"
            className="group inline-flex items-center gap-2 bg-[#1a120a] text-[#f5ebd9] px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#8a6a1f] transition-colors flex-shrink-0"
          >
            Meet The Founder
            <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};


// ---------------- FOUNDER ----------------
const Founder = () => (
  <section id="founder" className="relative py-32 md:py-44 px-6 md:px-12 bg-[#0b0805]">
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="lg:col-span-5 relative"
      >
        <div className="relative aspect-[4/5] overflow-hidden">
          <img src={MEDIA.founder} alt="Tia Smith" className="w-full h-full object-cover" />
          <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-[#D4AF37]/60 -z-0 hidden md:block" />
        </div>
        <div className="absolute -top-4 -left-4 w-24 h-24 border border-[#D4AF37]/60 hidden md:block" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.15 }}
        className="lg:col-span-7"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-8 h-px bg-[#D4AF37]" />
          <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">07 — Founder</span>
        </div>
        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-8">
          Meet <em className="text-[#D4AF37] not-italic">Tia Smith</em>.
        </h2>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-10">
          {["CEO", "Visionary", "Leader", "Educator", "Entrepreneur"].map((t) => (
            <span key={t} className="text-xs tracking-[0.3em] uppercase text-white/60">{t}</span>
          ))}
        </div>

        <div className="space-y-6 text-white/70 text-lg leading-relaxed">
          <p>
            As a mother, business owner, and industry leader, Tia Smith understands the transformative
            power of education and opportunity.
          </p>
          <p>
            Through The Braid Summit, she is committed to helping others turn their talent into thriving
            careers while creating pathways for long-term success and community impact.
          </p>
        </div>

        <div className="mt-12 flex items-center gap-6">
          <div className="w-16 h-px bg-[#D4AF37]" />
          <p className="font-display text-2xl italic text-white/90">
            "Your gift will make room for you."
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

// ---------------- TESTIMONIALS ----------------
const Testimonials = () => {
  const items = [
    {
      quote:
        "The Braid Summit didn't just teach me technique — it taught me to see myself as a CEO. My booth rent days are over.",
      name: "Amara Johnson",
      role: "Salon Owner · Atlanta, GA",
      img: MEDIA.student,
    },
    {
      quote:
        "I tripled my monthly revenue within six months of completing the Academy. The business modules changed everything.",
      name: "Destiny Williams",
      role: "Master Braider · Houston, TX",
      img: MEDIA.artistic,
    },
    {
      quote:
        "This community made me believe my craft is a career — and my career can build generational wealth.",
      name: "Imani Carter",
      role: "Beauty Educator · Newark, NJ",
      img: MEDIA.founder,
    },
  ];

  return (
    <section className="relative py-32 md:py-44 px-6 md:px-12 bg-[#100c07]">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">08 — Voices</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Stories of <em className="text-[#D4AF37] not-italic">transformation</em>.
            </h2>
          </div>
          <div className="flex items-center gap-1 text-[#D4AF37]">
            {[...Array(5)].map((_, i) => (
              <Star key={`star-${i}`} size={18} fill="#D4AF37" />
            ))}
            <span className="ml-3 text-sm text-white/60">From our graduates</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-white/10">
          {items.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
              className="bg-[#0b0805] p-10 flex flex-col"
            >
              <Quote size={32} className="text-[#D4AF37] mb-8" />
              <p className="font-display text-xl text-white/90 leading-relaxed mb-10 flex-grow italic">"{t.quote}"</p>
              <div className="flex items-center gap-4 pt-8 border-t border-white/10">
                <div className="w-12 h-12 overflow-hidden grayscale">
                  <img src={t.img} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-xs text-white/50 tracking-wide mt-1">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- SPONSOR MODAL ----------------
const SPONSOR_LEVELS = [
  { key: "title", label: "Title Sponsor — $25,000+" },
  { key: "platinum", label: "Platinum — $10,000" },
  { key: "gold", label: "Gold — $5,000" },
  { key: "silver", label: "Silver — $2,500" },
  { key: "community", label: "Community — $1,000" },
  { key: "custom", label: "Custom / In-Kind / Other" },
];

const SponsorModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    level: "gold",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setStatus("idle");
      setErrorMsg("");
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const selected = SPONSOR_LEVELS.find((l) => l.key === form.level);
      await axios.post(`${API}/sponsor`, {
        ...form,
        level: selected ? selected.label : form.level,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto"
          onClick={onClose}
          data-testid="sponsor-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35 }}
            className="relative w-full max-w-2xl bg-[#15110a] border border-[#D4AF37]/40 my-8 sm:my-0"
            onClick={(e) => e.stopPropagation()}
            data-testid="sponsor-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/60 hover:text-[#D4AF37] transition-colors"
              aria-label="Close"
              data-testid="sponsor-modal-close"
            >
              <X size={22} />
            </button>

            <div className="p-8 sm:p-12">
              {status === "success" ? (
                <div className="text-center py-8" data-testid="sponsor-success">
                  <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37] flex items-center justify-center">
                    <Check size={28} className="text-[#D4AF37]" />
                  </div>
                  <p className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-4">Inquiry Received</p>
                  <h3 className="font-display text-3xl sm:text-4xl mb-4 leading-tight">Thank you, {form.contact_name.split(" ")[0] || "friend"}.</h3>
                  <p className="text-white/65 leading-relaxed max-w-md mx-auto">
                    We've received your sponsorship inquiry for <span className="text-[#D4AF37]">{form.company}</span>. The full Sponsorship Deck is attached to your confirmation email — or download it directly below.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href={`${API}/sponsor/deck`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="sponsor-success-deck-download"
                      className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-8 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] transition-colors"
                    >
                      Download Deck (PDF)
                      <ArrowUpRight size={14} />
                    </a>
                    <button
                      onClick={onClose}
                      className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-8 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:border-[#D4AF37] hover:text-white transition-colors"
                      data-testid="sponsor-success-close"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-px bg-[#D4AF37]" />
                    <span className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase">Become A Sponsor</span>
                  </div>
                  <h3 className="font-display text-3xl sm:text-4xl mb-3 leading-tight">Partner with <em className="text-[#D4AF37] not-italic">the future</em> of beauty.</h3>
                  <p className="text-white/55 text-sm leading-relaxed mb-8">
                    Tell us a bit about your organization and how you'd like to support The Braid Summit. We'll respond within 2 business days with a sponsorship deck and custom activation ideas.
                  </p>

                  <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Company / Organization *</label>
                      <input
                        type="text"
                        required
                        value={form.company}
                        onChange={update("company")}
                        data-testid="sponsor-company"
                        className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder-white/30 transition-colors"
                        placeholder="e.g. Sephora"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Contact Name *</label>
                      <input
                        type="text"
                        required
                        value={form.contact_name}
                        onChange={update("contact_name")}
                        data-testid="sponsor-name"
                        className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder-white/30 transition-colors"
                        placeholder="Jane Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="sponsor-phone"
                        className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder-white/30 transition-colors"
                        placeholder="(555) 555-5555"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        data-testid="sponsor-email"
                        className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder-white/30 transition-colors"
                        placeholder="you@company.com"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Sponsorship Level *</label>
                      <select
                        required
                        value={form.level}
                        onChange={update("level")}
                        data-testid="sponsor-level"
                        className="w-full bg-[#15110a] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white transition-colors"
                      >
                        {SPONSOR_LEVELS.map((l) => (
                          <option key={l.key} value={l.key} className="bg-[#15110a]">{l.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Message</label>
                      <textarea
                        rows={4}
                        value={form.message}
                        onChange={update("message")}
                        data-testid="sponsor-message"
                        className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder-white/30 resize-none transition-colors"
                        placeholder="Tell us about your goals, brand values, or any specific activation ideas..."
                      />
                    </div>

                    {status === "error" && (
                      <p className="sm:col-span-2 text-xs text-red-400 tracking-wide" data-testid="sponsor-error">{errorMsg}</p>
                    )}

                    <div className="sm:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-2">
                      <button
                        type="submit"
                        disabled={status === "loading"}
                        data-testid="sponsor-submit"
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      >
                        {status === "loading" ? (
                          <><Loader2 size={16} className="animate-spin" /> Sending</>
                        ) : (
                          <>Submit Inquiry <ArrowUpRight size={14} /></>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium text-white/60 hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                    </div>

                    <p className="sm:col-span-2 text-[10px] text-white/30 tracking-wide leading-relaxed mt-2">
                      By submitting, you agree to be contacted by The Braid Summit team regarding sponsorship opportunities. We respect your privacy and never share your information.
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};


// ---------------- WHY SPONSOR ----------------
const WhySponsor = ({ onOpen }) => {
  const benefits = [
    {
      n: "01",
      icon: <Users size={22} />,
      title: "High-intent audience",
      body: "Reach braiders, salon owners, beauty entrepreneurs, students, and creatives — professionals actively reinvesting in their craft.",
    },
    {
      n: "02",
      icon: <Heart size={22} />,
      title: "Authentic alignment",
      body: "As a certified B-Corporation, every sponsorship signals genuine cultural investment — not surface-level participation.",
    },
    {
      n: "03",
      icon: <Sparkles size={22} />,
      title: "Long-tail content & PR",
      body: "Editorial photography, magazine features, social content, and earned press — your brand is featured for 6+ months post-event.",
    },
    {
      n: "04",
      icon: <ShieldCheck size={22} />,
      title: "Measurable impact",
      body: "B-Corp programmatic support qualifies as both a marketing investment and a measurable ESG / DE&I line — favored by foundation reporting.",
    },
  ];

  return (
    <section id="why-sponsor" className="relative py-32 md:py-44 px-6 md:px-12 bg-[#100c07] overflow-hidden warm-glow">
      <div className="absolute inset-0 opacity-15 pointer-events-none">
        <img src={MEDIA.texture} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080806] via-transparent to-[#080806]" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 mb-20 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">For Sponsors & Partners</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Invest where <em className="text-[#D4AF37] not-italic">talent</em> meets opportunity.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[#D4AF37]/30">
            <p className="text-white/65 leading-relaxed">
              Brands that partner with The Braid Summit don't just sponsor an event — they help shape the
              future of an entire industry while reaching one of the most engaged professional audiences in beauty.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mb-16">
          {benefits.map((b) => (
            <motion.div
              key={b.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: parseInt(b.n) * 0.08 }}
              className="bg-[#100c07] p-8 md:p-10 group hover:bg-[#1a150e] transition-colors duration-500"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-all duration-500">
                  {b.icon}
                </div>
                <span className="font-display text-3xl text-white/15 group-hover:text-[#D4AF37]/60 transition-colors duration-500">{b.n}</span>
              </div>
              <h3 className="font-display text-xl mb-3 leading-tight">{b.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{b.body}</p>
            </motion.div>
          ))}
        </div>

        {/* Deck download row */}
        <div className="relative border border-[#D4AF37]/30 bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a] to-[#1a1408] p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-2 flex md:justify-center">
            <div className="relative w-20 h-24 bg-black border border-[#D4AF37] flex flex-col items-center justify-center group">
              <div className="absolute top-0 right-0 w-4 h-4 bg-[#D4AF37]" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />
              <span className="text-[8px] tracking-[0.3em] text-[#D4AF37] mb-1">PDF</span>
              <span className="font-display text-xs text-white text-center px-2 leading-tight">Deck</span>
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-3">Sponsorship Deck · 2026</div>
            <h3 className="font-display text-2xl md:text-3xl mb-3 leading-tight">
              Take the full deck to your team.
            </h3>
            <p className="text-white/55 text-sm leading-relaxed">
              Tiers, benefits, audience metrics, and activation ideas — everything your team needs to evaluate a partnership in under five minutes.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <button
              type="button"
              onClick={onOpen}
              data-testid="why-sponsor-download-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] transition-colors gold-glow"
            >
              Get The Deck
              <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
            </button>
            <button
              type="button"
              onClick={onOpen}
              data-testid="why-sponsor-inquire-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              Become A Sponsor
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};


// ---------------- FINAL CTA ----------------
const FinalCTA = ({ onOpenSponsor, onOpenApply, onOpenPartner }) => {
  return (
  <section id="join" className="relative py-32 md:py-44 px-6 md:px-12 overflow-hidden grain-overlay">
    <div className="absolute inset-0">
      <img src={MEDIA.texture} alt="" className="w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-black/70 to-[#050505]" />
    </div>

    <div className="relative z-10 max-w-[1200px] mx-auto text-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="flex justify-center items-center gap-3 mb-10">
          <div className="w-8 h-px bg-[#D4AF37]" />
          <span className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase">09 — Join Us</span>
          <div className="w-8 h-px bg-[#D4AF37]" />
        </div>

        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
          Your Talent Has <em className="text-[#D4AF37] not-italic">Value</em>.
          <br />
          Your Knowledge Has <em className="text-[#D4AF37] not-italic">Power</em>.
          <br />
          Your Future Starts <em className="text-[#D4AF37] not-italic">Here</em>.
        </h2>

        <p className="mt-10 text-white/60 max-w-2xl mx-auto text-lg leading-relaxed">
          Whether you're stepping into your gift, scaling your studio, or seeking to fund the future of
          beauty education — there's a seat at the summit for you.
        </p>

        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            type="button"
            onClick={onOpenApply}
            data-testid={HOME.finalApplyBtn}
            className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-10 py-5 text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] transition-all duration-300 gold-glow cursor-pointer"
          >
            Apply Now
            <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
          </button>
          <button
            type="button"
            onClick={onOpenPartner}
            data-testid={HOME.finalPartnerBtn}
            className="group inline-flex items-center gap-3 border border-[#D4AF37]/60 text-white px-10 py-5 text-sm tracking-[0.2em] uppercase font-medium hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all duration-300 cursor-pointer"
          >
            Partner With Us
            <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
          </button>
          <button
            type="button"
            onClick={onOpenSponsor}
            data-testid={HOME.finalSponsorBtn}
            className="group inline-flex items-center gap-3 border border-white/30 text-white px-10 py-5 text-sm tracking-[0.2em] uppercase font-medium hover:border-white hover:bg-white/5 transition-all duration-300 cursor-pointer"
          >
            Become A Sponsor
            <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
          </button>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

// ---------------- FOOTER ----------------
const Footer = () => (
  <footer className="relative bg-black border-t border-white/10 px-6 md:px-12 py-20">
    <div className="max-w-[1400px] mx-auto">
      <div className="grid md:grid-cols-12 gap-12 mb-16">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 overflow-hidden border border-[#D4AF37]/40 bg-[#15110a]">
              <img src={MEDIA.logo} alt="The Braid Summit logo" className="w-full h-full object-cover" />
            </div>
            <div className="font-display text-xl tracking-tight">The Braid Summit</div>
          </div>
          <p className="text-white/60 text-sm leading-relaxed max-w-md">
            A certified B-Corporation dedicated to elevating the beauty industry through education,
            entrepreneurship, community, and empowerment.
          </p>
        </div>

        <div className="md:col-span-2">
          <div className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-5">Platform</div>
          <ul className="space-y-3 text-sm text-white/70">
            <li><a href="#about" className="hover:text-[#D4AF37] transition-colors">About</a></li>
            <li><a href="#programs" className="hover:text-[#D4AF37] transition-colors">Programs</a></li>
            <li><a href="#impact" className="hover:text-[#D4AF37] transition-colors">Impact</a></li>
            <li><a href="#founder" className="hover:text-[#D4AF37] transition-colors">Founder</a></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-5">Engage</div>
          <ul className="space-y-3 text-sm text-white/70">
            <li><a href="#apply" className="hover:text-[#D4AF37] transition-colors">Apply</a></li>
            <li><a href="#partner" className="hover:text-[#D4AF37] transition-colors">Partner</a></li>
            <li><a href="#sponsor" className="hover:text-[#D4AF37] transition-colors">Sponsor</a></li>
            <li><a href="#join" className="hover:text-[#D4AF37] transition-colors">Newsletter</a></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-5">Connect</div>
          <p className="text-white/70 text-sm leading-relaxed">
            info@braidsummit.com
            <br />
            Atlanta, GA · Houston, TX · Newark, NJ
          </p>
        </div>
      </div>

      <div className="pt-10 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <p className="text-xs text-white/40 tracking-wide">
          © {new Date().getFullYear()} The Braid Summit. A Certified B-Corporation. All Rights Reserved.
        </p>
        <div className="flex items-center gap-6 text-xs text-white/40">
          <a href="#" className="hover:text-[#D4AF37] transition-colors">Privacy</a>
          <a href="#" className="hover:text-[#D4AF37] transition-colors">Terms</a>
          <a href="#" className="hover:text-[#D4AF37] transition-colors">Accessibility</a>
        </div>
      </div>
    </div>
  </footer>
);

// ---------------- ROOT ----------------
const HomePage = () => {
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  return (
    <div className="bg-[#0b0805] text-white font-body min-h-screen relative">
      <Nav />
      <Hero />
      <About />
      <MissionVision />
      <ValuesMarquee />
      <Impact />
      <Programs />
      <ChampagneQuote />
      <MixerRecap />
      <IvoryPromises />
      <Founder />
      <Testimonials />
      <WhySponsor onOpen={() => setSponsorOpen(true)} />
      <FinalCTA
        onOpenSponsor={() => setSponsorOpen(true)}
        onOpenApply={() => setApplyOpen(true)}
        onOpenPartner={() => setPartnerOpen(true)}
      />
      <Footer />
      <SponsorModal open={sponsorOpen} onClose={() => setSponsorOpen(false)} />
      <InquiryModal open={applyOpen} variant="apply" onClose={() => setApplyOpen(false)} />
      <InquiryModal open={partnerOpen} variant="partner" onClose={() => setPartnerOpen(false)} />
    </div>
  );
};

export default HomePage;
